// DualListPickerDialog — the ONE shared available→selected picker modal
// (BO UI Standard §11: Add Reagents / Instruments / recipients / any
// multi-assign).  Built ON the shared <AppDialog> shell — never a bespoke
// modal.  Props-only: the host owns the data and receives the final selection
// on OK; nothing is persisted here.
import { useMemo, useState, type ReactNode } from 'react';
import { AppDialog } from '../AppDialog';
import './dualListPicker.css';

export interface DualListItem {
  id: string;
  label: string;
  /** Optional secondary text appended muted after the label. */
  hint?: string;
}

export interface DualListPickerDialogProps {
  open: boolean;
  title: ReactNode;
  /** Tabler icon for the dialog header (default ti-list-check). */
  icon?: string;
  /** Full catalog of pickable items. */
  available: DualListItem[];
  /** Ids selected when the dialog opens. */
  selectedIds: string[];
  /** OK — the host applies (and audits/persists via its own handler). */
  onOk: (selectedIds: string[]) => void;
  onCancel: () => void;
  /** Rows per side page (default 8). */
  pageSize?: number;
  availableTitle?: string;
  selectedTitle?: string;
  width?: number;
}

function usePagedFilter(items: DualListItem[], pageSize: number) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? items.filter((i) => `${i.label} ${i.hint ?? ''}`.toLowerCase().includes(needle)) : items;
  }, [items, q]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages);
  const rows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  return { q, setQ: (v: string) => { setQ(v); setPage(1); }, page: safePage, setPage, pages, rows, total: filtered.length };
}

function Side({ title, items, pageSize, action, actionKind }: {
  title: string;
  items: DualListItem[];
  pageSize: number;
  action: (id: string) => void;
  actionKind: 'add' | 'remove';
}) {
  const p = usePagedFilter(items, pageSize);
  return (
    <div className="dlp-side">
      <div className="dlp-side-hdr">
        <span>{title}</span>
        <span className="dlp-count">{p.total}</span>
      </div>
      <input
        className="dlp-search"
        placeholder="Search…"
        value={p.q}
        onChange={(e) => p.setQ(e.target.value)}
      />
      <ul className="dlp-list">
        {p.rows.length === 0 && <li className="dlp-empty">No items</li>}
        {p.rows.map((it) => (
          <li key={it.id} className="dlp-row">
            <button
              type="button"
              className={`dlp-btn dlp-btn-${actionKind}`}
              onClick={() => action(it.id)}
              aria-label={`${actionKind === 'add' ? 'Add' : 'Remove'} ${it.label}`}
              title={actionKind === 'add' ? 'Add' : 'Remove'}
            >
              {actionKind === 'add' ? '+' : '−'}
            </button>
            <span className="dlp-row-label">
              {it.label}
              {it.hint && <span style={{ color: 'var(--ai-muted)' }}> — {it.hint}</span>}
            </span>
          </li>
        ))}
      </ul>
      {p.pages > 1 && (
        <div className="dlp-pager">
          <button type="button" disabled={p.page <= 1} onClick={() => p.setPage(p.page - 1)}>‹</button>
          <span>Page {p.page} of {p.pages}</span>
          <button type="button" disabled={p.page >= p.pages} onClick={() => p.setPage(p.page + 1)}>›</button>
        </div>
      )}
    </div>
  );
}

export function DualListPickerDialog({
  open, title, icon = 'ti-list-check', available, selectedIds, onOk, onCancel,
  pageSize = 8, availableTitle = 'Available', selectedTitle = 'Selected', width = 720,
}: DualListPickerDialogProps) {
  const [picked, setPicked] = useState<string[]>(selectedIds);
  const [openedFor, setOpenedFor] = useState(open ? selectedIds : null as string[] | null);
  // Re-seed the working selection each time the dialog (re)opens.
  if (open && openedFor !== selectedIds) { setOpenedFor(selectedIds); setPicked(selectedIds); }

  const pickedSet = useMemo(() => new Set(picked), [picked]);
  const availableRows = useMemo(() => available.filter((i) => !pickedSet.has(i.id)), [available, pickedSet]);
  const selectedRows = useMemo(() => available.filter((i) => pickedSet.has(i.id)), [available, pickedSet]);

  return (
    <AppDialog
      open={open}
      title={title}
      icon={icon}
      width={width}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="ca-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => onOk(picked)}>OK</button>
        </>
      }
    >
      <div className="dlp-wrap">
        <Side title={availableTitle} items={availableRows} pageSize={pageSize} actionKind="add"
          action={(id) => setPicked((prev) => [...prev, id])} />
        <Side title={selectedTitle} items={selectedRows} pageSize={pageSize} actionKind="remove"
          action={(id) => setPicked((prev) => prev.filter((x) => x !== id))} />
      </div>
    </AppDialog>
  );
}
